import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useAuth } from '../../context/AuthContext';
import { contentAPI } from '../../services/api';
import { isValidPhoneInput, normalizePhoneInput, phoneInputProps } from '../../utils/phoneInput.js';
import './Auth.css';

const AdminLogin = () => {
  const { data: siteBranding } = useQuery(
    ['site-settings-branding'],
    async () => {
      const res = await contentAPI.getSiteSettings();
      return res.data?.data ?? res.data ?? {};
    },
    { staleTime: 5 * 60 * 1000, retry: 1 }
  );
  const brandName =
    String(siteBranding?.site_brand_name || 'INVINCIBLE').trim() || 'INVINCIBLE';
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('phone'); // for otp mode: 'phone' | 'otp'
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { sendOtp, loginWithOtp } = useAuth();
  const navigate = useNavigate();

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    const digits = normalizePhoneInput(phone);
    if (!isValidPhoneInput(digits)) {
      setError('Enter a valid 10-digit mobile number starting with 6–9.');
      return;
    }
    setLoading(true);
    const res = await sendOtp(digits);
    setLoading(false);
    if (!res.success) {
      setError(res.message || 'Failed to send OTP');
      return;
    }
    setPhone(digits);
    setStep('otp');
    setOtp('');
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (!otp.trim()) {
      setError('Enter the OTP.');
      return;
    }
    setLoading(true);
    const res = await loginWithOtp(phone, otp.trim());
    setLoading(false);
    if (res.success) {
      navigate('/');
    } else {
      setError(res.message || 'Login failed');
    }
  };

  return (
    <div className="auth-page">
      <main className="auth-container">
        <div className="auth-card">
          <div className="auth-brand">
            <img src="/images/logo.png" alt={brandName} className="auth-brand-logo" />
            <span className="auth-brand-badge">Admin Portal</span>
          </div>
          <h2>Admin Portal Access</h2>
          <p className="auth-subtitle">Login with your registered mobile number.</p>
          {error && <div className="error-message">{error}</div>}
          {step === 'phone' && (
            <form onSubmit={handleSendOtp}>
              <div className="form-group">
                <label htmlFor="phone">Admin Mobile Number</label>
                <div className="auth-input-wrap">
                  <span className="auth-input-icon" aria-hidden="true">+91</span>
                  <input
                    {...phoneInputProps}
                    id="phone"
                    value={phone}
                    onChange={(e) => setPhone(normalizePhoneInput(e.target.value))}
                    placeholder="Enter 10-digit mobile"
                    required
                  />
                </div>
              </div>
              <button type="submit" disabled={loading || !isValidPhoneInput(phone)} className="submit-btn">
                {loading ? 'Sending OTP...' : 'Send OTP'}
              </button>
            </form>
          )}
          {step === 'otp' && (
            <form onSubmit={handleVerifyOtp}>
              <p className="auth-step-note">
                OTP sent to <strong>+91 {phone}</strong>
              </p>
              <div className="form-group">
                <label htmlFor="otp">Enter OTP</label>
                <div className="auth-input-wrap auth-input-wrap-no-prefix">
                  <input
                    type="text"
                    id="otp"
                    name="otp"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="6-digit OTP"
                    maxLength={6}
                    required
                  />
                </div>
              </div>
              <button type="submit" disabled={loading || otp.length < 4} className="submit-btn">
                {loading ? 'Verifying...' : 'Verify & Login'}
              </button>
              <button
                type="button"
                className="auth-link-btn"
                onClick={() => {
                  setStep('phone');
                  setOtp('');
                  setError('');
                }}
              >
                Change mobile number
              </button>
            </form>
          )}
        </div>
      </main>
      <footer className="auth-footer">
        <div className="auth-footer-brand">{brandName}</div>
        <div className="auth-footer-links">
          <a href="#" onClick={(e) => e.preventDefault()}>Terms of Service</a>
          <a href="#" onClick={(e) => e.preventDefault()}>Privacy Policy</a>
          <a href="#" onClick={(e) => e.preventDefault()}>Contact Us</a>
        </div>
        <div className="auth-footer-copy">© {new Date().getFullYear()} {brandName}. All Rights Reserved.</div>
      </footer>
    </div>
  );
};

export default AdminLogin;
